import { useDataTableContext } from "./store/DataTableProvider";
import { PageProjectDataTable } from "./PageProjectDataTable";
import { PageProjectTabs } from "./PageProjectTabs";
import { useSetAppHeader } from "providers/AppHeaderProvider";
import { ProjectBreadcrumbs } from "components/HeaderBreadcrumbs/ProjectBreadcrumbs/ProjectBreadcrumbs";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { useEffect, useMemo } from "react";
import { ProjectHeaderNav } from "components/HeaderNav/ProjectHeaderNav";
import { useProjectFolderContext } from "./folders/ProjectFoldersProvider";
import { useSetTourCondition } from "providers/TourProvider/TourProvider.hooks";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { TenantType } from "types/constants";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { captureException } from "@sentry/react";
import { isEqual } from "lodash";

export interface PageGenericDataTablesProps {
  tableKey: string | undefined;
}
export const PageGenericDataTables = ({
  tableKey,
}: PageGenericDataTablesProps) => {
  const tables = useDataTableContext((s) => s.tables);
  const folders = useProjectFolderContext((s) => s.getSortedFolders());
  const selectedTable = useDataTableContext((s) => {
    return s.tables.find((table) => table.key === tableKey);
  });

  const syncStatus = useDataTableContext((s) => s.syncStatus);
  const project = useDataTableContext((s) => {
    const project = s.project;
    return {
      id: project.id,
      key: project.key,
      name: project.name,
      isDemo: project.is_demo,
      icon: project.icon,
      iconColor: project.icon_color,
      iconText: project.icon_text,
    };
  }, isEqual);
  const role = useProjectPermissionStore((s) => s.permissions.role_display);
  const currentTenant = useTenantContext((s) => s.currentTenant);
  const hasEditPermission = useProjectPermissionStore(
    (s) => s.permissions.edit,
  );
  const isCurrentUsersPersonalSpace = useUserContext(
    (s) => currentTenant.id === s.personalSpace?.id,
  );

  // Report syncing errors to Sentry
  useEffect(() => {
    if (syncStatus.status === "error") {
      captureException(syncStatus.error, {
        extra: {
          actionName: syncStatus.details.name,
          actionArgs: syncStatus.details.args,
        },
      });

      // Log to console when running a development build
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log(syncStatus.error);
      }
    }
  }, [syncStatus]);

  // set header
  const [breadcrumbs, nav] = useMemo(() => {
    return [
      <ProjectBreadcrumbs
        key={"breadcrumbs"}
        project={project}
        tables={tables}
        folders={folders}
        currentTenant={currentTenant}
        selectedTable={selectedTable}
        isCurrentUsersPersonalSpace={isCurrentUsersPersonalSpace}
      />,
      <ProjectHeaderNav
        key={"nav"}
        project={project}
        folders={folders}
        selectedTableId={selectedTable?.id}
        currentTenant={currentTenant}
        tables={tables}
        isCurrentUsersPersonalSpace={isCurrentUsersPersonalSpace}
      />,
    ];
  }, [
    project,
    tables,
    folders,
    currentTenant,
    selectedTable,
    isCurrentUsersPersonalSpace,
  ]);
  useSetAppHeader({ header: breadcrumbs, nav });
  useSetTourCondition("isProjectAdmin", role === "Admin" || role === "Owner");
  useSetTourCondition("isProjectEditor", hasEditPermission);
  useSetTourCondition(
    "isPersonalSpace",
    currentTenant.tenantType === TenantType.PERSONAL,
  );
  return selectedTable ? (
    <PageProjectDataTable tableKey={selectedTable.key} />
  ) : (
    <PageProjectTabs />
  );
};
